import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { UserClaims } from '../../+state/user-identity/user-identity-settings.models';
import { selectCurrentUserData } from '../../+state/user-identity/user-identity-settings.selectors';
import { RootState } from '../interfaces/rootState.interface';

export const firebaseAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const store = inject(Store<RootState>);
  const auth = inject(Auth);

  return store.select(selectCurrentUserData).pipe(
    take(1),
    switchMap((userData) => {
      // Prefer the app-session token from the store. The onboarding wizard
      // (EDUS-2681) is a Firebase session that is deliberately NOT stored (it
      // is not an app session), so when the store is empty fall back to the
      // live Firebase ID token — otherwise wizard API calls go out tokenless
      // and 401 on the first attempt. Normal app calls always have a store
      // token, so this fallback never runs for them.
      const token$: Observable<string | undefined> = userData?.token
        ? of(userData.token)
        : auth.currentUser
          ? from(auth.currentUser.getIdToken())
          : of(undefined);

      return token$.pipe(
        switchMap((token) => {
          const reqWithAuth = addTokenToRequest(req, token, userData?.claims);

          return next(reqWithAuth).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status !== 401) {
                return throwError(() => error);
              }
              const user = auth.currentUser;

              if (!user) {
                return throwError(
                  () => new Error('User not authenticated for token refresh.')
                );
              }

              return from(user.getIdToken(true)).pipe(
                switchMap((freshToken) => {
                  const reqWithFreshToken = addTokenToRequest(
                    req,
                    freshToken,
                    userData?.claims
                  );
                  return next(reqWithFreshToken);
                })
              );
            })
          );
        })
      );
    })
  );
};

function addTokenToRequest(
  req: HttpRequest<unknown>,
  token: string | undefined | null,
  claims?: UserClaims
): HttpRequest<unknown> {
  const headers: Record<string, string> = {
    'x-cdw-app-name': 'cdw-amplified-edu-dev',
  };

  // Extract customer IDs from Firebase claims (set by spog-backend during login)
  if (claims?.core?.customerId) {
    headers['x-cdw-customer-id'] = claims.core.customerId.toString();
  }
  if (claims?.providers?.google?.customerId) {
    headers['x-google-customer-id'] = claims.providers.google.customerId;
  }

  // Add Authorization header if token exists
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Add claims header if claims exist (for audit logging)
  if (claims) {
    headers['x-cdw-claims'] = JSON.stringify(claims);
  }

  if (claims?.core?.sessionId) {
    headers['x-session-id'] = claims.core.sessionId;
  }

  return req.clone({ setHeaders: headers });
}
